import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BadgeDollarSign,
  CheckCircle2,
  Construction,
  FileWarning,
  Handshake,
  Heart,
  Link2Off,
  MapPin,
  PackageCheck,
  Pause,
  Play,
  ReceiptText,
  RotateCcw,
  ShieldCheck,
  ShieldX,
  Sparkles,
  Store,
  Zap,
} from 'lucide-react';
import {
  applyCollision,
  applyCleanDodge,
  calculateLevel,
  clampLane,
  COLLISION_Y,
  createInitialRunState,
  GAME_DURATION_MS,
  generateSpawnPattern,
  getGrade,
  getGradeLabel,
  getComboMultiplier,
  getLevelConfig,
  LOCALOPS_CHAIN,
  readBestScore,
  TICK_MS,
  writeBestScore,
} from './streetSprintGame';
import './jgStreetSprint.css';

const ICONS = {
  request: MapPin,
  helper: Handshake,
  complete: CheckCircle2,
  proof: ReceiptText,
  credit: Zap,
  boost: BadgeDollarSign,
  business: Store,
  drop: PackageCheck,
  spam: AlertTriangle,
  broken: Link2Off,
  fake: ShieldX,
  auth: ShieldCheck,
  cone: Construction,
  proofWarning: FileWarning,
};

const CHAIN_LABELS = {
  request: 'Request',
  helperMatch: 'Match',
  taskComplete: 'Done',
  proofReceipt: 'Proof',
  actionCredit: 'Credit',
  jgkBoost: 'Boost',
};

export const STREET_SPRINT_SOUND_URLS = {
  damage: `${process.env.PUBLIC_URL || ''}/sounds/streetSprint/damage.mp3`,
  death: `${process.env.PUBLIC_URL || ''}/sounds/streetSprint/death.mp3`,
  good: `${process.env.PUBLIC_URL || ''}/sounds/streetSprint/good.mp3`,
};

export function getCollisionSound(item, nextRun) {
  if (item?.type === 'good') return 'good';
  if (item?.type === 'bad') return nextRun?.lives <= 0 ? 'death' : 'damage';
  return null;
}

export function getStreakLabel(combo) {
  if (combo >= 18) return 'Legend streak';
  if (combo >= 12) return 'City takeover';
  if (combo >= 8) return 'Hot route';
  if (combo >= 4) return 'Flow building';
  return '';
}

function getScreenEffect(item, nextRun, activeElapsedMs) {
  if (!item?.type) return null;
  const base = {
    id: `${activeElapsedMs}-${item.instanceId}-${item.type}`,
    label: item.label,
  };

  if (item.type === 'bad') {
    const type = nextRun?.lives <= 0 ? 'death' : 'damage';
    return {
      ...base,
      type,
      expiresAt: activeElapsedMs + (type === 'death' ? 1200 : 640),
    };
  }

  return {
    ...base,
    type: item.effect === 'boost' ? 'boost' : 'good',
    expiresAt: activeElapsedMs + 640,
  };
}

const createGameState = () => ({
  activeElapsedMs: 0,
  boostUntilMs: 0,
  finished: false,
  highestLevel: 1,
  items: [],
  lane: 1,
  level: 1,
  levelMessage: '',
  moveEvent: null,
  run: createInitialRunState(),
  screenEffect: null,
  soundEvent: null,
  spawnClockMs: 0,
  spawnId: 0,
});

const formatTime = (ms) =>
  String(Math.max(0, Math.ceil((GAME_DURATION_MS - ms) / 1000))).padStart(2, '0');

const useReducedMotion = () => {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(Boolean(media.matches));

    const update = () => setReducedMotion(Boolean(media.matches));
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return reducedMotion;
};

function ItemIcon({ name }) {
  const Icon = ICONS[name] || Sparkles;
  return <Icon aria-hidden="true" size={20} strokeWidth={2.4} />;
}

function StreetEnvironment() {
  return (
    <div className="street-sprint-environment" aria-hidden="true">
      <div className="street-sprint-skyline">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="street-sprint-atmosphere">
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="street-sprint-billboard">
        <strong>LOCALOPS</strong>
        <small>Proof run live</small>
      </div>
      <div className="street-sprint-storefronts street-sprint-storefronts--left">
        <span />
        <span />
        <span />
      </div>
      <div className="street-sprint-storefronts street-sprint-storefronts--right">
        <span />
        <span />
        <span />
      </div>
      <span className="street-sprint-streetlight street-sprint-streetlight--left" />
      <span className="street-sprint-streetlight street-sprint-streetlight--right" />
      <span className="street-sprint-traffic-signal" />
      <span className="street-sprint-parked-car street-sprint-parked-car--left" />
      <span className="street-sprint-parked-car street-sprint-parked-car--right" />
    </div>
  );
}

function RunnerFigure() {
  return (
    <>
      <div className="street-sprint-runner" aria-hidden="true">
        <span className="street-sprint-runner-shadow" />
        <span className="street-sprint-runner-head" />
        <span className="street-sprint-runner-hair" />
        <span className="street-sprint-runner-torso" />
        <span className="street-sprint-runner-pack" />
        <span className="street-sprint-runner-arm street-sprint-runner-arm--left" />
        <span className="street-sprint-runner-arm street-sprint-runner-arm--right" />
        <span className="street-sprint-runner-leg street-sprint-runner-leg--left" />
        <span className="street-sprint-runner-leg street-sprint-runner-leg--right" />
        <span className="street-sprint-runner-shoe street-sprint-runner-shoe--left" />
        <span className="street-sprint-runner-shoe street-sprint-runner-shoe--right" />
      </div>
      <span className="street-sprint-sr-only">Street runner</span>
    </>
  );
}

export default function JGStreetSprint() {
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState('ready');
  const [countdown, setCountdown] = useState(3);
  const [game, setGame] = useState(createGameState);
  const [bestScore, setBestScore] = useState(() => readBestScore());
  const mountedRef = useRef(true);
  const soundsRef = useRef({});
  const touchStartXRef = useRef(null);

  const levelConfig = useMemo(() => getLevelConfig(game.level), [game.level]);
  const grade = getGrade(game.run.score);
  const boostActive = game.run.boostUntilMs > game.activeElapsedMs;
  const comboMultiplier = getComboMultiplier(game.run.combo);
  const streakLabel = getStreakLabel(game.run.combo);
  const screenEffectType = game.screenEffect?.type;
  const trackClassName = [
    'street-sprint-track',
    phase === 'playing' ? 'is-playing' : '',
    reducedMotion ? 'is-reduced-motion' : '',
    boostActive ? 'has-boost-active' : '',
    screenEffectType ? `has-screen-${screenEffectType}` : '',
    game.run.combo >= 8 ? 'is-on-streak' : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (typeof Audio === 'undefined' || process.env.NODE_ENV === 'test') return undefined;

    soundsRef.current = Object.entries(STREET_SPRINT_SOUND_URLS).reduce(
      (sounds, [key, src]) => {
        const audio = new Audio(src);
        audio.preload = 'auto';
        audio.volume = key === 'damage' ? 0.7 : 0.82;
        sounds[key] = audio;
        return sounds;
      },
      {},
    );

    return () => {
      Object.values(soundsRef.current).forEach((audio) => {
        audio.pause();
        audio.removeAttribute('src');
      });
      soundsRef.current = {};
    };
  }, []);

  const playSound = useCallback((name) => {
    const audio = soundsRef.current[name];
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    } catch (error) {
      // Ignore browser autoplay or decoding failures; gameplay must continue.
    }
  }, []);

  useEffect(() => {
    if (!game.soundEvent) return;
    playSound(game.soundEvent.name);
  }, [game.soundEvent, playSound]);

  const moveLane = useCallback((delta) => {
    setGame((current) => {
      const nextLane = clampLane(current.lane + delta);
      if (nextLane === current.lane) return current;
      return {
        ...current,
        lane: nextLane,
        moveEvent: {
          direction: nextLane > current.lane ? 'right' : 'left',
          id: `${current.activeElapsedMs}-${current.lane}-${nextLane}`,
        },
      };
    });
  }, []);

  const resetRun = useCallback(() => {
    setGame(createGameState());
    setCountdown(3);
    setPhase('countdown');
  }, []);

  const pauseGame = useCallback(() => {
    setPhase((current) => (current === 'playing' ? 'paused' : current));
  }, []);

  const resumeGame = useCallback(() => {
    setPhase((current) => (current === 'paused' ? 'playing' : current));
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const key = event.key.toLowerCase();
      const isStartKey = key === ' ' || key === 'enter';

      if ((phase === 'ready' || phase === 'ended') && isStartKey) {
        event.preventDefault();
        resetRun();
        return;
      }

      if (phase === 'paused' && isStartKey) {
        event.preventDefault();
        resumeGame();
        return;
      }

      if (phase !== 'playing') return;

      if (key === 'arrowleft' || key === 'a') {
        event.preventDefault();
        moveLane(-1);
      } else if (key === 'arrowright' || key === 'd') {
        event.preventDefault();
        moveLane(1);
      } else if (key === 'p') {
        event.preventDefault();
        pauseGame();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveLane, pauseGame, phase, resetRun, resumeGame]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) pauseGame();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [pauseGame]);

  useEffect(() => {
    if (phase !== 'countdown') return undefined;

    const intervalId = window.setInterval(() => {
      if (!mountedRef.current) return;
      setCountdown((current) => {
        if (current <= 0) {
          setPhase('playing');
          return 0;
        }
        return current - 1;
      });
    }, 650);

    return () => window.clearInterval(intervalId);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;

    const intervalId = window.setInterval(() => {
      if (!mountedRef.current) return;
      setGame((current) => {
        const activeElapsedMs = current.activeElapsedMs + TICK_MS;
        const nextLevel = calculateLevel(activeElapsedMs);
        const config = getLevelConfig(nextLevel);
        let spawnClockMs = current.spawnClockMs + TICK_MS;
        let spawnId = current.spawnId;
        let items = current.items.map((item) => ({
          ...item,
          y: item.y + config.speed,
        }));
        let run = current.run;
        let screenEffect = null;
        let soundEvent = null;

        items = items.reduce((nextItems, item) => {
          const collides = item.y >= COLLISION_Y && item.lane === current.lane;
          if (collides) {
            run = applyCollision(run, item, { activeElapsedMs });
            screenEffect = getScreenEffect(item, run, activeElapsedMs);
            const soundName = getCollisionSound(item, run);
            if (soundName) {
              soundEvent = {
                id: `${activeElapsedMs}-${item.instanceId}-${soundName}`,
                name: soundName,
              };
            }
            return nextItems;
          }

          const cleanDodge =
            item.type === 'bad' &&
            !item.dodged &&
            item.y >= COLLISION_Y &&
            item.lane !== current.lane;
          const nextItem = cleanDodge
            ? {
              ...item,
              dodged: true,
            }
            : item;

          if (cleanDodge) {
            run = applyCleanDodge(run, item, { activeElapsedMs });
            screenEffect = {
              id: `${activeElapsedMs}-${item.instanceId}-dodge`,
              label: item.label,
              type: 'dodge',
              expiresAt: activeElapsedMs + 480,
            };
          }

          if (nextItem.y <= 108) {
            nextItems.push(nextItem);
          }
          return nextItems;
        }, []);

        if (spawnClockMs >= config.spawnEveryMs) {
          const spawned = generateSpawnPattern({
            level: nextLevel,
            spawnId,
          });
          items = [...items, ...spawned];
          spawnClockMs = 0;
          spawnId += 1;
        }

        const currentScreenEffect =
          current.screenEffect?.expiresAt > activeElapsedMs ? current.screenEffect : null;

        return {
          ...current,
          activeElapsedMs,
          finished: activeElapsedMs >= GAME_DURATION_MS || run.lives <= 0,
          highestLevel: Math.max(current.highestLevel, nextLevel),
          items,
          level: nextLevel,
          levelMessage:
            nextLevel > current.level
              ? `Level ${nextLevel} - ${config.label}`
              : current.levelMessage,
          screenEffect: screenEffect || currentScreenEffect,
          run,
          soundEvent: soundEvent || current.soundEvent,
          spawnClockMs,
          spawnId,
        };
      });
    }, TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing' || !game.finished) return;
    setPhase('ended');
    setBestScore(writeBestScore(game.run.score));
  }, [game.finished, game.run.score, phase]);

  const handlePointerDown = (event) => {
    touchStartXRef.current = event.clientX;
  };

  const handlePointerUp = (event) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null || phase !== 'playing') return;
    const delta = event.clientX - startX;
    if (Math.abs(delta) < 34) return;
    moveLane(delta > 0 ? 1 : -1);
  };

  const renderItem = (item) => (
    <div
      className={`street-sprint-item street-sprint-item--${item.type} street-sprint-item--${item.id}`}
      data-item-type={item.type}
      data-testid={`street-sprint-item-${item.instanceId}`}
      key={item.instanceId}
      style={{
        '--lane': item.lane,
        '--item-y': `${item.y}%`,
        '--item-opacity': Math.min(1, 0.68 + item.y / 115),
        '--item-scale': 0.82 + item.y / 340,
      }}
    >
      <span className="street-sprint-item-beacon" aria-hidden="true" />
      <span className="street-sprint-item-icon">
        <ItemIcon name={item.icon} />
      </span>
      <span className="street-sprint-item-copy">
        <strong>{item.label}</strong>
        <small>{item.type === 'good' ? 'Collect' : 'Dodge'}</small>
      </span>
    </div>
  );

  return (
    <main className="street-sprint-page">
      <section className="street-sprint-shell">
        <header className="street-sprint-header">
          <div>
            <p className="street-sprint-eyebrow">JG LocalOps arcade</p>
            <h1>JG Street Sprint</h1>
            <p>
              Run the neighborhood loop: collect requests, match helpers, finish
              tasks, prove the work, and build simulated Action Credit energy.
            </p>
          </div>
          <div className="street-sprint-best">
            <span>Best Street Score</span>
            <strong>{bestScore}</strong>
          </div>
        </header>

        <div className="street-sprint-layout">
          <section
            aria-label="JG Street Sprint game area"
            className={trackClassName}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            style={{
              '--road-speed': `${Math.max(360, 790 - game.level * 72)}ms`,
            }}
          >
            <StreetEnvironment />
            <div className="street-sprint-road" aria-hidden="true">
              <span className="street-sprint-curb street-sprint-curb--left" />
              <span className="street-sprint-curb street-sprint-curb--right" />
              <span className="street-sprint-lane-light street-sprint-lane-light--left" />
              <span className="street-sprint-lane-light street-sprint-lane-light--right" />
              <span className="street-sprint-finish-line" />
            </div>

            <div className="street-sprint-hud">
              <div>
                <span>Street Score</span>
                <strong>{game.run.score}</strong>
              </div>
              <div>
                <span>Combo</span>
                <strong>{game.run.combo}x</strong>
              </div>
              <div>
                <span>Level</span>
                <strong>{game.level}</strong>
              </div>
              <div>
                <span>Time</span>
                <strong>{formatTime(game.activeElapsedMs)}</strong>
              </div>
            </div>

            <div className="street-sprint-lives" aria-label={`${game.run.lives} lives`}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Heart
                  aria-hidden="true"
                  className={index < game.run.lives ? 'is-full' : ''}
                  key={index}
                  size={20}
                />
              ))}
            </div>

            <div
              className="street-sprint-combo-meter"
              aria-label={`Combo ${game.run.combo}, multiplier ${comboMultiplier}x`}
              style={{ '--combo-fill': `${Math.min(100, game.run.combo * 9)}%` }}
            >
              <span>Flow x{comboMultiplier}</span>
              <strong>{streakLabel || `${game.run.combo} combo`}</strong>
              <i aria-hidden="true" />
            </div>

            <div
              className="street-sprint-chain-meter"
              aria-label={`Proof chain step ${game.run.chainIndex} of ${LOCALOPS_CHAIN.length}`}
            >
              {LOCALOPS_CHAIN.map((chainKey, index) => (
                <span
                  className={index < game.run.chainIndex ? 'is-complete' : ''}
                  key={chainKey}
                >
                  <small>{CHAIN_LABELS[chainKey]}</small>
                </span>
              ))}
            </div>

            <div
              className={[
                'street-sprint-player',
                game.moveEvent ? `is-moving-${game.moveEvent.direction}` : '',
                boostActive ? 'is-boosted' : '',
                screenEffectType === 'damage' || screenEffectType === 'death' ? 'is-hit' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-lane={game.lane}
              data-effect={screenEffectType || 'none'}
              data-testid="street-sprint-player"
              key={game.moveEvent?.id || 'runner'}
              style={{ '--lane': game.lane }}
            >
              <RunnerFigure />
            </div>

            {game.items.map(renderItem)}

            {game.run.lastOutcome ? (
              <div
                className={`street-sprint-outcome street-sprint-outcome--${
                  game.run.lastOutcome.startsWith('Hit') ? 'bad' : 'good'
                }`}
                data-effect={screenEffectType || 'none'}
                aria-live="polite"
              >
                {game.run.lastOutcome}
              </div>
            ) : null}

            {streakLabel && phase === 'playing' ? (
              <div className="street-sprint-streak" aria-live="polite">
                {streakLabel}
              </div>
            ) : null}

            {phase === 'ready' ? (
              <div className="street-sprint-overlay">
                <p className="street-sprint-eyebrow">60 second run</p>
                <h2>Build the proof chain. Dodge the friction.</h2>
                <p>
                  Use arrows, A/D, swipe, or the lane buttons. Level difficulty
                  climbs during active play. Collect proof-chain wins, and thread
                  past friction for Clean Dodge points.
                </p>
                <button type="button" onClick={resetRun}>
                  <Play aria-hidden="true" size={18} />
                  Start run
                </button>
              </div>
            ) : null}

            {phase === 'countdown' ? (
              <div className="street-sprint-countdown" aria-live="assertive">
                {countdown > 0 ? countdown : 'GO'}
              </div>
            ) : null}

            {phase === 'paused' ? (
              <div className="street-sprint-overlay">
                <p className="street-sprint-eyebrow">Paused</p>
                <h2>Street clock stopped.</h2>
                <p>Scoring and level timers are paused until you resume.</p>
                <button type="button" onClick={resumeGame}>
                  <Play aria-hidden="true" size={18} />
                  Resume
                </button>
              </div>
            ) : null}

            {phase === 'ended' ? (
              <div className="street-sprint-overlay">
                <p className="street-sprint-eyebrow">Run complete</p>
                <h2>Grade {grade}: {getGradeLabel(grade)}</h2>
                <div className="street-sprint-summary">
                  <span>Score <strong>{game.run.score}</strong></span>
                  <span>Best <strong>{bestScore}</strong></span>
                  <span>Highest level <strong>{game.highestLevel}</strong></span>
                  <span>Best combo <strong>{game.run.highestCombo}x</strong></span>
                </div>
                <button type="button" onClick={resetRun}>
                  <RotateCcw aria-hidden="true" size={18} />
                  One more run
                </button>
              </div>
            ) : null}

            {game.levelMessage && phase === 'playing' ? (
              <div className="street-sprint-level" aria-live="polite">
                {game.levelMessage}
              </div>
            ) : null}

            {boostActive ? (
              <div className="street-sprint-boost" aria-live="polite">
                JGK-style boost active
              </div>
            ) : null}
          </section>

          <aside className="street-sprint-panel">
            <div>
              <p className="street-sprint-eyebrow">Proof chain</p>
              <h2>Request to credit, fast.</h2>
              <ol>
                <li>Request</li>
                <li>Helper Match</li>
                <li>Task Complete</li>
                <li>Proof Receipt</li>
                <li>Action Credit Energy</li>
                <li>JGK-style Score Boost</li>
                <li>Clean Dodge Bonus</li>
              </ol>
            </div>
            <div className="street-sprint-panel-note">
              <strong>Simulation only.</strong>
              <span>
                This game does not award real JGK, real Action Credit, trust
                status, payouts, or LocalOps privileges.
              </span>
            </div>
            <div className="street-sprint-controls">
              <button
                aria-label="Move left"
                disabled={phase !== 'playing'}
                onClick={() => moveLane(-1)}
                type="button"
              >
                Left
              </button>
              <button
                aria-label="Move right"
                disabled={phase !== 'playing'}
                onClick={() => moveLane(1)}
                type="button"
              >
                Right
              </button>
            </div>
            <button
              className="street-sprint-pause"
              disabled={phase !== 'playing' && phase !== 'paused'}
              onClick={phase === 'paused' ? resumeGame : pauseGame}
              type="button"
            >
              {phase === 'paused' ? (
                <Play aria-hidden="true" size={17} />
              ) : (
                <Pause aria-hidden="true" size={17} />
              )}
              {phase === 'paused' ? 'Resume' : 'Pause'}
            </button>
          </aside>
        </div>
      </section>
    </main>
  );
}
